import { Grid } from '@mui/material';
import { RoutalLoader, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import { DriversIcon } from '../../../assets/svg/Plan/MenuBar/DriversIcon';
import { StopsIcon } from '../../../assets/svg/Plan/MenuBar/StopsIcon';
import { getPlanStatusColor } from '../../../constants/Colors';
import { PlanFlat } from '../../../domain/plan/PlanFlat';
import Text from '../../../react-components/Text';
import { PlanStatusIcon } from '../shared/PlanStatusIcon';
import { PlanStatusBar } from './PlanStatusBar';

interface PlannerPlansDayListProps {
  plan?: PlanFlat;
  style?: React.CSSProperties;
  withTooltip?: boolean;
  isLoading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const PlanPill = ({
  plan,
  style = {},
  withTooltip = false,
  isLoading = false,
  onClick,
}: PlannerPlansDayListProps) => {
  const clickableStyles = onClick ? { cursor: `pointer` } : {};

  if (isLoading)
    return (
      <Grid
        container
        direction="column"
        wrap="nowrap"
        justifyContent="center"
        alignItems="center"
        style={{
          zIndex: 10,
          height: `70px`,
          width: `400px`,
          position: `absolute`,
          top: 0,
          left: 0,
          backgroundColor: RoutalPalette.neutral00,
        }}
      >
        <RoutalLoader />
      </Grid>
    );

  if (!plan) return;

  const statusColorMedium = getPlanStatusColor(plan.status);

  return (
    <Grid
      key={plan.id}
      container
      direction="row"
      wrap="nowrap"
      alignItems="center"
      onClick={onClick}
      style={{
        userSelect: `none`,
        height: `70px`,
        ...clickableStyles,
        ...style,
      }}
      gap="8px"
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        alignItems="center"
        justifyContent="center"
        style={{
          width: `unset`,
          height: `100%`,
          borderRadius: `5px`,
          backgroundColor: statusColorMedium,
          padding: `0px 6px`,
        }}
      >
        <PlanStatusIcon
          status={plan.status}
          style={{ color: getPlanStatusColor(plan.status, `dark`), marginBottom: `1px` }}
        />
      </Grid>

      <Grid container direction="column" wrap="nowrap" gap="6px" style={{ width: `calc(100% - 34px)` }}>
        {(plan.label ?? ``) !== `` ? (
          <>
            {withTooltip ? (
              <RoutalTooltip id="plan-id" placement="top" title={plan.label!}>
                <Text
                  variant="body"
                  style={{ ...clickableStyles, textOverflow: `ellipsis`, overflow: `hidden`, whiteSpace: `nowrap` }}
                >
                  {plan.label}
                </Text>
              </RoutalTooltip>
            ) : (
              <Text
                variant="body"
                style={{ ...clickableStyles, textOverflow: `ellipsis`, overflow: `hidden`, whiteSpace: `nowrap` }}
              >
                {plan.label}
              </Text>
            )}
          </>
        ) : (
          <Text
            variant="body"
            style={{ fontWeight: 500, cursor: `pointer`, color: RoutalPalette.secondary00 }}
            intlId="no_name"
          />
        )}

        <Grid container direction="row" wrap="nowrap" alignItems="center" gap="10px">
          <Grid item container direction="row" alignItems="center" wrap="nowrap" gap="4px" style={{ width: `unset` }}>
            <DriversIcon color={RoutalPalette.secondary00} height="10px" />

            <Text style={{ ...clickableStyles, fontSize: `10px`, color: RoutalPalette.secondary20 }}>
              {plan.total_drivers}
            </Text>
          </Grid>

          <Grid item container direction="row" alignItems="center" wrap="nowrap" gap="4px" style={{ width: `unset` }}>
            <StopsIcon color={RoutalPalette.secondary00} height="10px" width="10px" />

            <Text style={{ ...clickableStyles, fontSize: `10px`, color: RoutalPalette.secondary20 }}>
              {plan.total_stops}
            </Text>
          </Grid>
        </Grid>

        <PlanStatusBar
          planId={plan.id}
          pendingStops={plan.pending_stops ?? 0}
          incompleteStops={plan.incomplete_stops ?? 0}
          completedStops={plan.completed_stops ?? 0}
          canceledStops={plan.canceled_stops ?? 0}
          width="100%"
        />
      </Grid>
    </Grid>
  );
};
